/**
 * Async Tasks Provider
 *
 * Polls GET /threads/{threadId}/tasks every 5s and exposes live task status
 * to the Control Panel. Polling continues until the component unmounts —
 * new tasks may be created well after earlier tasks complete.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
  useState,
} from 'react';
import { useAppSelector } from '@/stores/store';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { fetchThreadTasks, mapApiTaskToCleanupTask } from '@/services/async-tasks.service';
import { appConfig } from '@/config/app-config';
import type { CleanupTask } from '@/components/control-panel/control-panel-dummy-data';

const MAX_RETRY_ATTEMPTS = 3;
const POLLING_INTERVAL = 5000;

interface AsyncTasksContextValue {
  tasks: CleanupTask[];
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const defaultContextValue: AsyncTasksContextValue = {
  tasks: [],
  isLoading: false,
  refetch: async () => {},
};

const AsyncTasksContext = createContext<AsyncTasksContextValue>(defaultContextValue);

interface AsyncTasksProviderProps {
  children: ReactNode;
  threadId: string | null;
}

export function AsyncTasksProvider({ children, threadId }: AsyncTasksProviderProps) {
  const [tasks, setTasks] = useState<CleanupTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const selectedAgent = useAppSelector(selectSelectedAgent);

  const retryCountRef = useRef<number>(0);
  const consecutiveErrorsRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchTasks = useCallback(async () => {
    if (!threadId) {
      setTasks([]);
      return;
    }

    const deploymentUrl = appConfig.API_BASE_URL;
    const agentId = selectedAgent?.id || 'optimize-clean-up-agent';
    const baseUrl = `${deploymentUrl}/agents/${agentId}`;

    setIsLoading(true);

    const [error, data] = await fetchThreadTasks(threadId, baseUrl);

    if (!isMountedRef.current) return;

    if (error) {
      consecutiveErrorsRef.current += 1;
      // Stop polling after 3 consecutive errors — backend is likely down or unavailable
      if (consecutiveErrorsRef.current >= MAX_RETRY_ATTEMPTS) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        console.warn(`Failed to fetch async tasks (stopped polling after ${MAX_RETRY_ATTEMPTS} errors):`, error);
        setIsLoading(false);
        return;
      }
      console.warn('Failed to fetch async tasks:', error);

      if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
        retryCountRef.current += 1;
        setTimeout(() => {
          if (isMountedRef.current) {
            fetchTasks();
          }
        }, 1000 * retryCountRef.current);
      }
    } else if (data) {
      const sorted = [...data.tasks].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
      setTasks(sorted.map(mapApiTaskToCleanupTask));
      retryCountRef.current = 0;
      consecutiveErrorsRef.current = 0;
    }

    setIsLoading(false);
  }, [threadId, selectedAgent]);

  // Clear tasks and reset error counters when thread changes
  useEffect(() => {
    setTasks([]);
    retryCountRef.current = 0;
    consecutiveErrorsRef.current = 0;
  }, [threadId]);

  // Fetch on mount and when threadId changes
  useEffect(() => {
    isMountedRef.current = true;
    fetchTasks();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchTasks]);

  // Poll every 5s; stop on component unmount (new tasks may appear after earlier ones finish)
  useEffect(() => {
    if (!threadId) return;

    pollingIntervalRef.current = setInterval(() => {
      if (!isMountedRef.current) return;
      fetchTasks();
    }, POLLING_INTERVAL);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [threadId, fetchTasks]);

  const contextValue: AsyncTasksContextValue = {
    tasks,
    isLoading,
    refetch: fetchTasks,
  };

  return (
    <AsyncTasksContext.Provider value={contextValue}>{children}</AsyncTasksContext.Provider>
  );
}

export function useAsyncTasks(): AsyncTasksContextValue {
  const context = useContext(AsyncTasksContext);
  if (!context) {
    throw new Error('useAsyncTasks must be used within an AsyncTasksProvider');
  }
  return context;
}
